/**
 * Beta K7 — "Days this runs". HTHD's hikes run Tuesdays and Thursdays and,
 * Johnny, 2026-10-30: "that should be configurable."
 *
 * One pure module, read by the booking gate, the customer calendar and the
 * settings card, so the three cannot disagree about what a list means.
 *
 *   · `service_types.run_days` stores the weekdays a CLIENT may book, 0=Sun …
 *     6=Sat, the numbering `CapacityRule.dayOfWeek` already uses.
 *   · EMPTY or ALL SEVEN = every day, today's behavior. In memory that is
 *     `null`, so "no restriction" is one value and never a list to walk.
 *   · A service that runs on no day is not a state: the write boundary refuses
 *     an empty tick list, and Hide is the switch for "not offered at all".
 *
 * WEEKDAY FRAME. A booking date is a calendar day in the facility's frame
 * (`YYYY-MM-DD`, validated at the boundary), so its weekday is a fact of that
 * string. `weekdayOfIso` reads it in UTC only because UTC is the one frame
 * with no offset to apply: it does not consult the clock, the server's zone or
 * the browser's. `new Date(iso).getDay()` in a browser west of Greenwich is
 * the yesterday bug this avoids, which is why the calendar takes each day's
 * answer from the server and does no weekday arithmetic of its own.
 */

/** The week, Sunday first, as `dayOfWeek` numbers it. */
export const WEEKDAY_NUMBERS = [0, 1, 2, 3, 4, 5, 6] as const;

const WEEKDAY_PLURAL = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'] as const;

/** A restriction, or `null` for every day. Never empty. */
export type RunDays = readonly number[] | null;

function isWeekday(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 && n <= 6;
}

/**
 * The stored list as the engine reads it. Anything that is not a real
 * restriction (absent, empty, every day, junk) is `null`, so a bad row can
 * only ever fail OPEN to today's behavior: turning a client away by accident
 * is the expensive mistake, and the desk still sees every request.
 */
export function runDaysFromStored(raw: unknown): RunDays {
  if (!Array.isArray(raw)) return null;
  const days = [...new Set(raw.filter(isWeekday))].sort((a, b) => a - b);
  if (days.length === 0 || days.length === WEEKDAY_NUMBERS.length) return null;
  return days;
}

/** What gets written: sorted and de-duplicated, every-day collapsed to `[]`. */
export function runDaysForStorage(days: RunDays): number[] {
  const parsed = runDaysFromStored(days);
  return parsed === null ? [] : [...parsed];
}

/** The weekday of a `YYYY-MM-DD` calendar day. See WEEKDAY FRAME above. */
export function weekdayOfIso(dateIso: string): number {
  return new Date(`${dateIso}T00:00:00.000Z`).getUTCDay();
}

export function serviceRunsOn(runDays: RunDays, dateIso: string): boolean {
  return runDays === null || runDays.includes(weekdayOfIso(dateIso));
}

const DAY_MS = 86_400_000;

/**
 * The calendar days from `startIso` through `endIso` that the service does not
 * run, inclusive. Seven days hold every weekday, so a longer range cannot add
 * a new answer and the walk stops there: a stay's length never costs more than
 * a week of arithmetic. A swapped pair is read in order: the customer schema
 * refuses one, but "no days" must never be how a request gets a free pass.
 */
export function offDaysBetween(runDays: RunDays, startIso: string, endIso: string): string[] {
  if (runDays === null) return [];
  const [from, to] = startIso <= endIso ? [startIso, endIso] : [endIso, startIso];
  const start = new Date(`${from}T00:00:00.000Z`).getTime();
  const end = new Date(`${to}T00:00:00.000Z`).getTime();
  const off: string[] = [];
  for (let i = 0; i < WEEKDAY_NUMBERS.length && start + i * DAY_MS <= end; i++) {
    const iso = new Date(start + i * DAY_MS).toISOString().slice(0, 10);
    if (!serviceRunsOn(runDays, iso)) off.push(iso);
  }
  return off;
}

function joinWords(words: readonly string[]): string {
  if (words.length <= 1) return words.join('');
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/** "Tuesdays and Thursdays". */
export function runDaysInWords(runDays: readonly number[]): string {
  return joinWords(runDays.map((d) => WEEKDAY_PLURAL[d] ?? '').filter((w) => w.length > 0));
}

/** "Adventure hike runs Tuesdays and Thursdays." One sentence, one place. */
export function runDaysSentence(serviceName: string, runDays: readonly number[]): string {
  return `${serviceName} runs ${runDaysInWords(runDays)}.`;
}
